import { createSlice } from "@reduxjs/toolkit"

import {
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../../redux/shared/paged-list.ts"
import { logout } from "../../../LoginPage/redux/auth.thunks.ts"
import { updateCustomer } from "../../redux/customers.thunks.ts"
import {
  fetchCustomerDetail,
  fetchCustomerLeads,
  fetchCustomerProjects
} from "./customer-detail.thunks.ts"
import { type CustomerDetailState } from "./customer-detail.types.ts"

const emptyList = { results: [], count: 0, next: null, fetching: false }

const initialState: CustomerDetailState = {
  customer: null,
  fetching: false,
  notFound: false,
  leads: emptyList,
  leadsRequestId: null,
  projects: emptyList,
  projectsRequestId: null
}

const customerDetailSlice = createSlice({
  name: "customerDetail",
  initialState,
  reducers: {
    clearCustomerDetail: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerDetail.pending, (state) => {
        state.fetching = true
        state.notFound = false
      })
      .addCase(fetchCustomerDetail.fulfilled, (state, action) => {
        state.fetching = false
        state.customer = action.payload
      })
      .addCase(fetchCustomerDetail.rejected, (state) => {
        state.fetching = false
        state.customer = null
        state.notFound = true
      })

      .addCase(fetchCustomerLeads.pending, (state, action) => {
        state.leadsRequestId = action.meta.requestId
        state.leads = {
          ...beforeFetch(
            state.leads,
            initialState.leads,
            action.meta?.arg?.isMore
          ),
          fetching: true
        }
      })
      .addCase(fetchCustomerLeads.fulfilled, (state, action) => {
        if (state.leadsRequestId !== action.meta.requestId) return
        state.leadsRequestId = null
        state.leads = {
          ...afterFetch(
            state.leads,
            { ...action.payload, fetching: false },
            action.meta?.arg?.isMore
          ),
          fetching: false
        }
      })
      .addCase(fetchCustomerLeads.rejected, (state, action) => {
        if (state.leadsRequestId !== action.meta.requestId) return
        state.leadsRequestId = null
        state.leads = {
          ...afterFailedFetch(
            state.leads,
            initialState.leads,
            action.meta?.arg?.isMore
          ),
          fetching: false
        }
      })

      .addCase(fetchCustomerProjects.pending, (state, action) => {
        state.projectsRequestId = action.meta.requestId
        state.projects = {
          ...beforeFetch(
            state.projects,
            initialState.projects,
            action.meta?.arg?.isMore
          ),
          fetching: true
        }
      })
      .addCase(fetchCustomerProjects.fulfilled, (state, action) => {
        if (state.projectsRequestId !== action.meta.requestId) return
        state.projectsRequestId = null
        state.projects = {
          ...afterFetch(
            state.projects,
            { ...action.payload, fetching: false },
            action.meta?.arg?.isMore
          ),
          fetching: false
        }
      })
      .addCase(fetchCustomerProjects.rejected, (state, action) => {
        if (state.projectsRequestId !== action.meta.requestId) return
        state.projectsRequestId = null
        state.projects = {
          ...afterFailedFetch(
            state.projects,
            initialState.projects,
            action.meta?.arg?.isMore
          ),
          fetching: false
        }
      })

      // The list's own write path is reused, so the saved record lands here too.
      .addCase(updateCustomer.fulfilled, (state, action) => {
        if (!state.customer || state.customer.id !== action.payload?.id) return
        state.customer = { ...state.customer, ...action.payload }
      })

      .addCase(logout.fulfilled, () => initialState)
  }
})

export const { clearCustomerDetail } = customerDetailSlice.actions

export default customerDetailSlice
